import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddBoxTwoToneIcon from "@mui/icons-material/AddBoxTwoTone";
import PlaceIcon from "@mui/icons-material/Place";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EventIcon from "@mui/icons-material/Event";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InputAdornment from "@mui/material/InputAdornment";
import { useFechas } from "../../context/FechaContext";

const formatDate = (date) => date.toISOString().split("T")[0];

const presetsFecha = [
  {
    label: "Hoy",
    calcular: () => {
      const hoy = new Date();
      return { desde: formatDate(hoy), hasta: formatDate(hoy) };
    },
  },
  {
    label: "Ayer",
    calcular: () => {
      const ayer = new Date();
      ayer.setDate(ayer.getDate() - 1);
      return { desde: formatDate(ayer), hasta: formatDate(ayer) };
    },
  },
  {
    label: "Últimos 7 días",
    calcular: () => {
      const hoy = new Date();
      const hace7 = new Date();
      hace7.setDate(hoy.getDate() - 6);
      return { desde: formatDate(hace7), hasta: formatDate(hoy) };
    },
  },
];

const CamposFecha = ({ desde, hasta, setDesde, setHasta, onPreset }) => {
  const [menuEl, setMenuEl] = useState(null);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        label="Desde"
        type="date"
        value={desde}
        onChange={(e) => setDesde(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        label="Hasta"
        type="date"
        value={hasta}
        onChange={(e) => setHasta(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <Tooltip title="Rangos rápidos">
        <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)}>
          <EventIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        {presetsFecha.map((preset) => (
          <MenuItem
            key={preset.label}
            onClick={() => {
              onPreset(preset.calcular());
              setMenuEl(null);
            }}
          >
            {preset.label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

// Barra superior de la vista: en desktop todo queda visible en línea; en
// mobile los filtros de fecha se esconden detrás de un ícono para no robarle
// espacio a las acciones que un operador necesita a mano (nuevo pedido,
// refrescar).
const EncabezadoPedidos = ({
  onNuevoPedido,
  onVerMapa,
  onRefrescar,
  loading,
  lastUpdated,
  esMobile,
  busqueda,
  onBusquedaChange,
}) => {
  const { desde, hasta, setDesde, setHasta } = useFechas();
  const [popoverEl, setPopoverEl] = useState(null);

  const aplicarPreset = ({ desde: d, hasta: h }) => {
    setDesde(d);
    setHasta(h);
  };

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Stack direction="row" spacing={1} alignItems="center">
        {esMobile ? (
          <>
            <Tooltip title="Filtrar por fecha">
              <IconButton onClick={(e) => setPopoverEl(e.currentTarget)} sx={{ backgroundColor: "#e0e0e0" }}>
                <FilterAltIcon />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(popoverEl)}
              anchorEl={popoverEl}
              onClose={() => setPopoverEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 1.5 }}>
                <CamposFecha desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} onPreset={aplicarPreset} />
              </Box>
            </Popover>
          </>
        ) : (
          <CamposFecha desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} onPreset={aplicarPreset} />
        )}

        <Tooltip title={lastUpdated ? `Actualizado ${lastUpdated.toLocaleTimeString()}` : "Actualizar"}>
          <span>
            <IconButton
              onClick={onRefrescar}
              disabled={loading}
              sx={{ backgroundColor: "#e0e0e0", "&:hover": { backgroundColor: "#d5d5d5" } }}
            >
              <RefreshIcon sx={{ color: "#333" }} />
            </IconButton>
          </span>
        </Tooltip>
        {!esMobile && lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            Actualizado {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
      </Stack>

      <Button
        variant="contained"
        startIcon={<AddBoxTwoToneIcon />}
        onClick={onNuevoPedido}
        sx={{ boxShadow: 2, backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" } }}
      >
        {esMobile ? "Nuevo" : "Nuevo Pedido (F5)"}
      </Button>

      <Button
        variant="outlined"
        startIcon={<PlaceIcon />}
        onClick={onVerMapa}
        sx={{
          color: "#2e7d32",
          borderColor: "#2e7d32",
          "&:hover": { backgroundColor: "#e8f5e9", borderColor: "#1b5e20" },
        }}
      >
        {esMobile ? "" : "Ver Mapa"}
      </Button>
    </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por cliente, dirección o chofer..."
        value={busqueda}
        onChange={(e) => onBusquedaChange(e.target.value)}
        sx={{ mt: 1, backgroundColor: "#ffffff" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: busqueda ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onBusquedaChange("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};

export default EncabezadoPedidos;
